export interface FactorExplanation {
  name: string;
  score: number;
  weight: number;
  contribution: number;
  interpretation: string;
  details: string;
}

export interface LocationForecast {
  locationId: string | null;
  latitude: number;
  longitude: number;
  date: string;
  timezone: string;
  sunriseTime: string | null;
  sunsetTime: string | null;
  sunriseQuality: number;
  sunsetQuality: number;
  sunriseCategory: string;
  sunsetCategory: string;
  sunriseExplanation: string | null;
  sunsetExplanation: string | null;
  sunriseFactorBreakdown: FactorExplanation[] | null;
  sunsetFactorBreakdown: FactorExplanation[] | null;
  goldenHourMorningStart: string | null;
  goldenHourMorningEnd: string | null;
  goldenHourEveningStart: string | null;
  goldenHourEveningEnd: string | null;
  blueHourMorningStart: string | null;
  blueHourMorningEnd: string | null;
  blueHourEveningStart: string | null;
  blueHourEveningEnd: string | null;
  cloudCover: number | null;
  cloudCoverLow: number | null;
  cloudCoverMid: number | null;
  cloudCoverHigh: number | null;
  humidity: number | null;
  visibility: number | null;
  windSpeed: number | null;
  temperature: number | null;
  pressure: number | null;
  factors: Record<string, unknown> | null;
  cached: boolean;
  fetchedAt: string;
  expiresAt: string;
}

export type LocationForecastPayload = Omit<
  LocationForecast,
  'timezone' | 'sunriseFactorBreakdown' | 'sunsetFactorBreakdown'
> & {
  timezone?: string | null;
  sunriseFactorBreakdown?: unknown;
  sunsetFactorBreakdown?: unknown;
};

function isFactorExplanation(value: unknown): value is FactorExplanation {
  if (!value || typeof value !== 'object') return false;

  const factor = value as Record<string, unknown>;
  return (
    typeof factor.name === 'string' &&
    typeof factor.score === 'number' &&
    typeof factor.weight === 'number' &&
    typeof factor.contribution === 'number' &&
    typeof factor.interpretation === 'string' &&
    typeof factor.details === 'string'
  );
}

function parseFactorBreakdown(value: unknown): FactorExplanation[] | null {
  if (value == null) return null;

  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!Array.isArray(parsed)) return null;

    const factors = parsed.filter(isFactorExplanation);
    return factors.length === parsed.length ? factors : null;
  } catch {
    return null;
  }
}

export function normalizeLocationForecast(
  forecast: LocationForecastPayload,
  fallbackTimezone: string
): LocationForecast {
  return {
    ...forecast,
    timezone: forecast.timezone || fallbackTimezone,
    sunriseFactorBreakdown: parseFactorBreakdown(forecast.sunriseFactorBreakdown),
    sunsetFactorBreakdown: parseFactorBreakdown(forecast.sunsetFactorBreakdown),
  };
}

export function isValidForecastCoordinates(latitude: number, longitude: number): boolean {
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
}

export function getForecastDate(timezone: string, now = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));

    return `${values.year}-${values.month}-${values.day}`;
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

export function getForecastDates(days: number, timezone: string, now = new Date()): string[] {
  const dayCount = Number.isFinite(days) ? Math.max(0, Math.floor(days)) : 0;
  const [year, month, day] = getForecastDate(timezone, now).split('-').map(Number);
  const start = Date.UTC(year, month - 1, day);

  return Array.from({ length: dayCount }, (_, index) =>
    new Date(start + index * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  );
}
